import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, MessageCircle, Send } from "lucide-react";
import { CONTACT } from "@/lib/i18n";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ConsultationModal = ({ open, setOpen, t, lang }) => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    company: "",
    role: "",
    email: "",
    phone: "",
    message: "",
  });

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.company || !form.phone) {
      toast.error(lang === "fr" ? "Veuillez remplir tous les champs obligatoires." : "Preencha os campos obrigatórios.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_BASE}/consultations`, { ...form, language: lang });
      toast.success(t.form.success);
      setForm({ name: "", company: "", role: "", email: "", phone: "", message: "" });
      setOpen(false);
    } catch (err) {
      console.error(err);
      toast.error(t.form.error);
    } finally {
      setLoading(false);
    }
  };

  const waMessage = encodeURIComponent(
    `Olá BID! Sou ${form.name || "[seu nome]"} da ${form.company || "[empresa]"}. ${form.message || "Gostaria de uma consultoria técnica."}`
  );
  const waHref = `https://wa.me/${CONTACT.whatsappRaw}?text=${waMessage}`;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="max-w-2xl bg-white border border-[#0a192f]/10 p-0 rounded-none overflow-hidden max-h-[92vh] overflow-y-auto"
        data-testid="consultation-modal"
      >
        <div className="bg-[#0a192f] text-white p-6 sm:p-8">
          <DialogHeader className="space-y-2 text-left">
            <DialogTitle className="font-display text-2xl sm:text-3xl font-black tracking-tight text-white">
              {t.form.title}
            </DialogTitle>
            <DialogDescription className="text-white/70 text-sm leading-relaxed">
              {t.form.subtitle}
            </DialogDescription>
          </DialogHeader>
        </div>

        <form onSubmit={onSubmit} className="p-6 sm:p-8 grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="consultation-form">
          <div className="sm:col-span-1">
            <label className="bid-label">{t.form.name} *</label>
            <input
              required
              value={form.name}
              onChange={setField("name")}
              className="bid-input"
              data-testid="form-name"
            />
          </div>
          <div className="sm:col-span-1">
            <label className="bid-label">{t.form.company} *</label>
            <input
              required
              value={form.company}
              onChange={setField("company")}
              className="bid-input"
              data-testid="form-company"
            />
          </div>
          <div className="sm:col-span-1">
            <label className="bid-label">{t.form.role}</label>
            <input
              value={form.role}
              onChange={setField("role")}
              className="bid-input"
              data-testid="form-role"
            />
          </div>
          <div className="sm:col-span-1">
            <label className="bid-label">{t.form.phone} *</label>
            <input
              required
              value={form.phone}
              onChange={setField("phone")}
              className="bid-input"
              data-testid="form-phone"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="bid-label">{t.form.email}</label>
            <input
              type="email"
              value={form.email}
              onChange={setField("email")}
              className="bid-input"
              data-testid="form-email"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="bid-label">{t.form.message}</label>
            <textarea
              rows={4}
              value={form.message}
              onChange={setField("message")}
              className="bid-input resize-none"
              data-testid="form-message"
            />
          </div>

          <div className="sm:col-span-2 flex flex-col sm:flex-row gap-3 pt-2">
            <button
              type="submit"
              disabled={loading}
              data-testid="form-submit"
              className="btn-primary flex-1 disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {t.form.submit}
            </button>
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="form-whatsapp"
              className="btn-secondary flex-1"
            >
              <MessageCircle className="w-4 h-4" />
              {t.form.whatsapp}
            </a>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ConsultationModal;
